import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, formatTranslation, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys, WorkflowTranslationKeys } from 'intl/keys';
import { translationKeyToString } from 'intl/types/translationKey';
import { useIntl } from 'react-intl';
import { Navigate, useLocation } from 'react-router-dom';
import { UrlParamName, useAppUrlParams } from 'hooks/navigation/useAppUrlParams';
import { useLatestRef } from 'hooks/useLatestRef';
import { useProject } from 'store/project';
import { useWorkflows } from 'store/workflow';
import { findStagesByWorkflowId } from 'store/workflow/model';
import { useProcesses } from 'store/process';
import { useProjectUsers } from 'store/projectUsers';
import { usersWithoutSuperAdminSelector } from 'shared/domain/user/selectors';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useAppNavigation } from 'contexts/navigation';
import { showErrorToastWithMessage } from 'services/toaster';
import { logError } from 'setup/logger/logError';
import { APP_PATHS } from 'setup/navigation/paths';
import type { WorkflowStageOutDto } from 'shared/domain/workflow/types';
import { hasApprovalRequired } from 'shared/domain/workflow/types';
import { compareObjectIds } from 'shared/utils/id/compare';
import { WORKFLOW_RETURN_TO_FIELDS_VISIBILITY } from 'views/fieldsVisibility/model';
import {
  createEmptyStageRow,
  computeSaveDiff,
  runWorkflowSave,
  WorkflowSavePhaseError,
  isWorkflowStageNameError,
} from './model';
import { WorkflowEditorPresentational } from './presentational';
import type { WorkflowEditorProps, WorkflowPersistExecutors, WorkflowStageRow } from './types';
import { useWorkflowDraft } from './useWorkflowDraft';

export function WorkflowEditorPage(): ReactElement {
  const workflowId = useAppUrlParams(UrlParamName.WORKFLOW_ID);
  const location = useLocation();
  const navigation = useAppNavigation();
  const intl = useIntl();
  const createDialog = useDialog();
  const { _id: projectId, organizationId } = useProject();

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const { items: allUsers } = useProjectUsers();

  const {
    workflows,
    updateWorkflow,
    createStage,
    updateStage,
    deleteStage,
    reorderStage,
  } = useWorkflows();
  const { processes } = useProcesses();

  const {
    workflow,
    workflowName,
    setWorkflowName,
    localStages,
    setLocalStages,
    getOriginalSnapshot,
    resetToServer,
  } = useWorkflowDraft(workflows, workflowId);
  const [workflowSaveLoading, setWorkflowSaveLoading] = useState(false);
  const [showNameErrors, setShowNameErrors] = useState(false);
  const localStagesRef = useLatestRef(localStages);

  const isLocked = Boolean(workflow?.isLocked);

  const isLockedWithoutApprovers = useMemo(() => {
    if (!workflow?.isLocked) {
      return false;
    }
    const activeStages = (workflow.stages ?? []).filter((stage) => !stage.deleted);
    const hasAnyApprovalStages = activeStages.some(
      (stage) => hasApprovalRequired({ approvalType: stage.approvalType })
    );
    return !hasAnyApprovalStages;
  }, [workflow]);

  const affectedProcessesText = useMemo(() => {
    if (!workflow) {
      return '';
    }

    const affected = processes.filter((process) =>
      compareObjectIds(process.workflowId, workflow._id)
    );

    if (affected.length === 0) {
      return '';
    }

    return affected
      .map((process) => {
        const translatedLabel = formatTranslation(intl, unsafeTranslationKey(process.label) );
        return `${process.code} (${translatedLabel})`;
      })
      .join(', ');
  }, [workflow, processes, intl]);

  const availableApprovers = useMemo(
    () => usersWithoutSuperAdminSelector(allUsers, organizationId),
    [allUsers, organizationId]
  );

  const executor = useMemo<WorkflowPersistExecutors>(
    () => ({
      updateWorkflow: (workflowWid, data, proj) =>
        updateWorkflow.execute(workflowWid, data, proj),
      createStage: (workflowWid, data, proj) =>
        createStage.execute(workflowWid, data, proj),
      updateStage: (workflowWid, stageId, data, proj) =>
        updateStage.execute(workflowWid, stageId, data, proj),
      deleteStage: (workflowWid, stageId, proj) =>
        deleteStage.execute(workflowWid, stageId, proj),
      reorderStage: (workflowWid, stageId, data, proj) =>
        reorderStage.execute(workflowWid, stageId, data, proj),
    }),
    [createStage, deleteStage, reorderStage, updateStage, updateWorkflow]
  );

  const navigateToList = useCallback((): void => {
    if (returnTo === WORKFLOW_RETURN_TO_FIELDS_VISIBILITY) {
      navigation.goToFieldsVisibility();
    } else {
      navigation.goToWorkflowConfiguration();
    }
  }, [navigation, returnTo]);

  const canPersist = useMemo(() => {
    const hasEmptyStageNames = localStages.some((s) => s.name.trim().length === 0);
    return Boolean(
      workflow &&
        projectId &&
        !workflowSaveLoading &&
        workflowName.trim() &&
        !hasEmptyStageNames
    );
  }, [workflow, projectId, workflowSaveLoading, workflowName, localStages]);

  const getStages = useCallback(
    (wfId: string): WorkflowStageOutDto[] =>
      findStagesByWorkflowId(workflows, wfId),
    [workflows]
  );

  const onPersistAll = useCallback(async (): Promise<void> => {
    if (!workflow || !projectId || !canPersist) {
      return;
    }
    const original = getOriginalSnapshot();
    if (!original) {
      return;
    }

    setWorkflowSaveLoading(true);
    try {
      const diff = computeSaveDiff(original, workflowName, localStages);
      await runWorkflowSave({
        workflowId: workflow._id,
        projectId,
        diff,
        executor,
        getStages: () => getStages(workflow._id),
      });
      setShowNameErrors(false);
      navigateToList();
    } catch (error) {
      if (isWorkflowStageNameError(error)) {
        setShowNameErrors(true);
        showErrorToastWithMessage(WorkflowTranslationKeys.creatorStageNameRequired);
        logError('Workflow save blocked: empty stage name', error);
      } else {
        const phase =
          error instanceof WorkflowSavePhaseError ? error.phase : 'unknown';
        const description = formatTranslation(intl, WorkflowTranslationKeys.saveFailedPhase , { phase }
        );
        logError(description, error);
        showErrorToastWithMessage(WorkflowTranslationKeys.saveFailedPhase, { phase });
      }
      // A phase that failed may follow saved ones: the draft starts again from what the server has.
      // A refused draft (validation) is kept for the user to fix.
      if (error instanceof WorkflowSavePhaseError) {
        resetToServer();
      }
    } finally {
      setWorkflowSaveLoading(false);
    }
  }, [
    canPersist,
    executor,
    getOriginalSnapshot,
    getStages,
    localStages,
    navigateToList,
    projectId,
    resetToServer,
    workflow,
    workflowName,
    intl,
  ]);

  const onSaveWorkflow = useCallback(async (): Promise<void> => {
    const saveDescription = affectedProcessesText
      ? formatTranslation(intl, WorkflowTranslationKeys.saveConfirmDescriptionWithProcesses, {
          processes: affectedProcessesText,
        })
      : formatTranslation(intl, WorkflowTranslationKeys.saveConfirmDescription);

    try {
      await createDialog({
        title: React.createElement(FormattedMessage, {
          id: WorkflowTranslationKeys.saveConfirmTitle,
        }),
        description: saveDescription,
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.save),
        ],
      });
    } catch {
      return;
    }
    await onPersistAll();
  }, [createDialog, onPersistAll, affectedProcessesText, intl]);

  const onGoBack = useCallback((): void => {
    if (returnTo === WORKFLOW_RETURN_TO_FIELDS_VISIBILITY) {
      navigation.goToFieldsVisibility();
    } else {
      navigation.goBack();
    }
  }, [navigation, returnTo]);

  const onStagesReorder = useCallback((ordered: WorkflowStageRow[]): void => {
    setLocalStages(ordered);
  }, [setLocalStages]);

  const onAddStage = useCallback((): void => {
    setLocalStages((prev) => {
      if (prev.length <= 1) {
        return [...prev, createEmptyStageRow()];
      }
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), createEmptyStageRow(), last];
    });
  }, [setLocalStages]);

  const onUpdateStage = useCallback(
    (localId: string, patch: Partial<WorkflowStageRow>): void => {
      setLocalStages((prev) => {
        const next = prev.map((r) => {
          if (!compareObjectIds(r._localId, localId)) {
            return r;
          }
          return { ...r, ...patch };
        });
        if ('name' in patch && next.every((r) => r.name.trim().length > 0)) {
          setShowNameErrors(false);
        }
        return next;
      });
    },
    [setLocalStages]
  );

  const onDeleteStage = useCallback(
    async (localId: string): Promise<void> => {
      const row = localStagesRef.current.find((r) => compareObjectIds(r._localId, localId));
      if (!row) {
        return;
      }
      setLocalStages((prev) => prev.filter((r) => !compareObjectIds(r._localId, localId)));
    },
    [localStagesRef, setLocalStages]
  );

  if (!workflowId) {
    return <Navigate to={APP_PATHS.project.settings.workflowConfiguration} replace />;
  }

  const props: WorkflowEditorProps = {
    workflow,
    workflowName,
    localStages,
    isLocked,
    isLockedWithoutApprovers,
    availableApprovers,
    workflowSaveLoading,
    showNameErrors,
    onChangeWorkflowName: setWorkflowName,
    onGoBack,
    onSaveWorkflow,
    onStagesReorder,
    onAddStage,
    onUpdateStage,
    onDeleteStage,
  };

  return <WorkflowEditorPresentational {...props} />;
}
